<template>
    <div class="container">
        <v-title :lineage="[{'Data Exports':'data-exports'}]">
            Add Data Exports
            <div slot="actions">
                <v-cancel :defaultRoute="{name: 'data-exports'}"></v-cancel>
                <r-button :loading="isLoading" type="primary" @click="save">{{singularItemType}}</r-button>
            </div>
        </v-title>
        <v-inline-notifications
            v-if="repeatMessage"
            type="positive"
            title="Data export successfully created"
            class="stack-l">
            <span slot="content">
                Would you like to add <r-button type="link" @click="newFromContinue">another data export</r-button> or go back to the <router-link :to="{name: 'data-exports'}">data exports list</router-link>?
            </span>
        </v-inline-notifications>
        <v-error-messages
            :has-frontend-errors="$v.model.$error || $v.recurringCheckbox.$error"
            :server-error="serverError"
            :predicates="errorMessages"
            class="stack-l"/>
        <v-form-tile>
            <h2 slot="title">Data exports</h2>
            <p slot="description" class="text-muted">Export your data once or on a recurring schedule,
                select the type of data you wish to export and customize which fields you can include in the export.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div
                            class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                data-cy="Name input"
                                :auto-focus="true"
                                label="Name"
                                v-model="model.name"
                                :validate="$v.model.name"/>
                        </div>
                    </div>
                    <div class="grid-content">
                        <resource-form
                            :resource="model.resource"
                            :validate="$v.model.resource"
                            v-on:update="updateResource"/>
                        <format-form
                            :format="model.format"
                            :validate="$v.model.format"
                            v-on:update="updateFormat"/>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile v-if="model.requiresFields">
            <h2 slot="title">Fields</h2>
            <p slot="description" class="text-muted">Choose the fields you want to export. Fewer fields will result in
                faster exports with a smaller file size.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <p class="stack-m">Drag and drop the fields to change the order of your export</p>
                    <div>
                        <fields-form
                            v-if="isCustomFieldsLoaded"
                            :available-fields="allResourceFields"
                            :default-fields="defaultFields"
                            :fields="model.fields"
                            :validate="$v.model.dateRange"
                            v-on:update="updateFields"/>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider" v-if="model.requiresFields"></div>
        <v-form-tile>
            <h2 slot="title">Filters</h2>
            <p slot="description" class="text-muted">Choose the date range you wish to export. This will be relative to
                the date when the export starts to run. You can also supply additional filters.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <date-range-form
                        :date-range="model.dateRange"
                        :validate="$v.model.dateRange"
                        v-on:update="updateDateRange"/>
                </div>
                <div class="tile-content">
                    <div class="tile-subtitle">
                        <h3>Filters</h3>
                    </div>
                    <filters-form
                        :available-filters="availableFilters"
                        :filter="filterString"
                        :resource="model.resource"
                        v-on:update="updateFilter"/>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Notifications</h2>
            <p slot="description" class="text-muted">Receive an email notification once your export is ready to
                download.</p>
            <div slot="content" class="tile">
                <email-notifications-form
                    :is-enabled="emailNotificationIsEnabled"
                    :email-notification="model.emailNotification"
                    v-on:update="updateEmailNotification"/>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Schedule</h2>
            <p slot="description" class="text-muted">You can schedule this export to run in the future and recur on
                regular basis.</p>
            <div slot="content" class="tile">
                <schedule-form
                    :recurring="model.recurring"
                    :enable-recurring="recurringIsEnabled"
                    :disable-toggle-recurring="toggleRecurringIsDisabled"
                    :validate="$v.model.recurring"
                    :checkbox-validation="$v.recurringCheckbox"
                    v-on:update="updateRecurring"/>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <v-cancel :defaultRoute="{name: 'data-exports'}"></v-cancel>
                <r-button :loading="isLoading" type="primary" @click="save">{{singularItemType}}</r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import {email, required} from 'vuelidate/lib/validators';
    import vTitle from '@/components/v-title';
    import {
        vCancel,
        vCheckbox,
        vErrorMessages,
        vFormTile,
        vIcon,
        vIconButton,
        vRadio,
        vSelect,
    } from '@/components/form';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import {DataExportModel, DateRangeModel, RecurringModel} from '@/common/models/data-export-model';
    import vInlineNotifications from '@/components/v-inline-notifications';
    import FieldsForm from '@/modules/data-exports/components/forms/fields-form';
    import ScheduleForm from '@/modules/data-exports/components/forms/schedule-form';
    import DateRangeForm from '@/modules/data-exports/components/forms/date-range-form';
    import ResourceForm from '@/modules/data-exports/components/forms/resource-form';
    import EmailNotificationsForm from '@/modules/data-exports/components/forms/email-notifications-form';
    import FormatForm from '@/modules/data-exports/components/forms/format-form';
    import FiltersForm from '@/modules/data-exports/components/forms/filters-form';
    import TransactionFilters from '@/modules/data-exports/definitions/filters/transaction-filters';
    import SubscriptionFilters from '@/modules/data-exports/definitions/filters/subscription-filters';
    import CustomerFilters from '@/modules/data-exports/definitions/filters/customer-filters';
    import PreferenceTypes from '@/store/preference-types';

    export default {
        name: 'data-export-form',
        mixins: [FormLoaderMixin],
        components: {
            ScheduleForm,
            vCheckbox,
            vCancel,
            vErrorMessages,
            vFormTile,
            vIcon,
            vIconButton,
            vInlineNotifications,
            vRadio,
            vTitle,
            FormatForm,
            ResourceForm,
            FieldsForm,
            EmailNotificationsForm,
            DateRangeForm,
            FiltersForm,
            vSelect,
        },
        props: {
            continue: {
                type: String,
                default: null,
            },
        },
        validations() {
            const validations = {
                model: {
                    name: {
                        required,
                    },
                    resource: {
                        required,
                    },
                    format: {
                        required,
                    },
                    recurring: {
                        instruction: {},
                        start: {},
                    },
                    fields: {},
                    dateRange: {},
                    emailNotification: {},
                },
                recurringCheckbox: {},
            };
            if (this.emailNotificationIsEnabled) {
                validations.model.emailNotification = {
                    required,
                    email: (value) => {
                        if (!value || !value.length) {
                            return false;
                        }
                        return value.every(email);
                    },
                };
            }
            if (this.recurringIsEnabled || this.toggleRecurringIsDisabled) {
                validations.model.recurring = {
                    instruction: {
                        required,
                    },
                    start: {
                        required,
                    },
                };
            }
            if (this.isFormUpdating && !this.recurringIsEnabled) {
                validations.recurringCheckbox = {
                    required,
                };
            }

            return validations;
        },
        computed: {
            availableFilters() {
                if (!this.model.resource) {
                    return {}; // empty filters
                }
                const map = {
                    [DataExportModel.Resources.transactions]: TransactionFilters,
                    [DataExportModel.Resources.customers]: CustomerFilters,
                    [DataExportModel.Resources.subscriptions]: SubscriptionFilters,
                };
                return map[this.model.resource];
            },
            singularItemType() {
                return this.isFormUpdating ? 'Update Export' : 'Export';
            },
            recurringIsEnabled() {
                return !!(this.model.recurring !== null);
            },
            toggleRecurringIsDisabled() {
                return !!(
                    this.isFormUpdating
                    && this.model.recurring
                    && this.model.recurring.instruction
                );
            },
            emailNotificationIsEnabled() {
                return !!(this.model.emailNotification !== null);
            },
            filterString() {
                return this.model.arguments ? this.model.arguments.filter || '' : '';
            },
            repeatMessage() {
                return this.continue !== null;
            },
            isCustomFieldsLoaded() {
                return this.customFields !== null;
            },
            allResourceFields() {
                return [
                    ...DataExportModel.getAllFieldsByResource(this.model.resource),
                    ...this.customFields,
                ];
            },
            defaultFields() {
                return DataExportModel.getDefaultFieldsByResource(this.model.resource);
            },
        },
        data() {
            const resource = DataExportModel.Resources.customers;

            return {
                customFields: null,
                customFieldsCache: {},
                model: new DataExportModel({
                    resource,
                    format: DataExportModel.Formats.csv,
                    fields: this.getFields(resource),
                }),
                isLoading: false,
                errorMessages: [
                    {
                        predicate: () => this.$v.model.name.$error,
                        message: 'The data export name is required.',
                    },
                    {
                        predicate: () => this.$v.model.resource.$error,
                        message: 'Please select a resource for the data export.',
                    },
                    {
                        predicate: () => this.$v.model.format.$error,
                        message: 'Please select a format for the data export.',
                    },
                    {
                        predicate: () => this.$v.model.emailNotification.$error,
                        message: 'Please provide a valid email address for the notifications.',
                    },
                    {
                        predicate: () => this.$v.model.recurring.instruction.$error
                            || this.$v.recurringCheckbox.$error,
                        message: ' Please select a scheduling option.',
                    },
                    {
                        predicate: () => this.$v.model.recurring.start.$error,
                        message: 'Please select a start time for the scheduling.',
                    },
                ],
            };
        },
        methods: {
            async fetchData() {
                this.customFields = await this.getCustomFields(this.model.resource);
                if (!this.isFormUpdating) {
                    return;
                }
                const {fields} = await this.$api(({dataExports}) => dataExports.get({
                    id: this.id,
                }));
                this.model = new DataExportModel(fields);
            },
            async create() {
                try {
                    const {id, ...data} = this.model.getPayload();
                    const {fields} = await this.$api(({dataExports}) => dataExports.queue({data: {...data}}));
                    this.model = new DataExportModel(fields);
                    this.updatePreferences();
                    this.$toast.positive(`Export successfully created`);
                    this.$router.push({name: 'data-exports'});
                } catch (error) {
                    this.serverError = error;
                    this.$toast.fromError(error, `Export could not be created`);
                }
            },
            async update() {
                const {id, ...data} = this.model.getPayload();
                try {
                    const {fields} = await this.$api(({dataExports}) => dataExports.update({id, data}));
                    this.model = new DataExportModel(fields);
                    this.$toast.positive(`Export successfully updated`);
                } catch (error) {
                    this.serverError = error;
                    this.$toast.fromError(error, `Export could not be updated`);
                }
            },
            getFields(resource) {
                const dataExportFields = this.getUserPreference(PreferenceTypes.dataExportFields, resource);
                if (!this.isFormUpdating
                    && dataExportFields) {
                    return dataExportFields.map(field => field.replace(/^\//, ''));
                }
                return DataExportModel.getDefaultFieldsByResource(resource);
            },
            async updatePreferences() {
                const data = this.model.fields;
                const id = this.model.resource;
                this.setUserPreference({type: PreferenceTypes.dataExportFields, id, data});
            },
            async getCustomFields(resourceName) {
                const schemas = DataExportModel.getCustomFieldsSchemas(resourceName);
                const allCustomFields = await Promise.all(schemas.map(
                    async ({resource, path}) => {
                        if (!(resource in this.customFieldsCache)) {
                            this.customFieldsCache[resource] = await this.$api(
                                ({customFields}) => customFields.getAll({resource}),
                            );
                        }
                        return {response: this.customFieldsCache[resource], path};
                    },
                ));
                return [].concat(...allCustomFields.map(
                    ({response, path}) => response.items.map(({fields}) => `${path}/${fields.name}`),
                ));
            },
            async updateResource(resource) {
                this.customFields = null;
                this.customFields = await this.getCustomFields(resource);
                this.model.resource = resource;
                this.model.fields = this.getFields(resource);
                this.updateFilter(null);
            },
            updateFormat(format) {
                this.model.format = format;
            },
            updateEmailNotification(emailNotification) {
                this.model.emailNotification = emailNotification;
            },
            updateRecurring(recurring) {
                // enable
                if (!this.model.recurring) {
                    this.model.recurring = new RecurringModel(recurring || {});
                    return;
                }
                // disable
                if (!recurring) {
                    this.model.recurring = recurring;
                    return;
                }
                // update params
                this.model.recurring = new RecurringModel({
                    ...this.model.recurring.getPayload(),
                    ...recurring,
                });
            },
            updateDateRange(payload) {
                if (!payload) {
                    this.model.dateRange = null;
                } else {
                    this.model.dateRange = new DateRangeModel({
                        ...this.model.dateRange || {},
                        ...payload,
                    });
                }
            },
            updateFilter(filter) {
                this.model.arguments = this.model.arguments || {};
                this.model.arguments = {
                    ...this.model.arguments,
                    filter,
                };
            },
            updateFields(fields) {
                this.model.fields = fields.length
                    ? fields
                    : this.getFields(this.model.resource);
            },
        },
    };
</script>
